import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { AdminTableSkeleton } from "../components/admin/AdminTableSkeleton";
import { MarkdownView } from "../components/dashboard/resources/MarkdownView";
import {
  useAdminResources,
  type AdminResourceWrite,
} from "../hooks/useAdminResources";
import type {
  ResourceCategory,
  ResourceView,
} from "../hooks/useResources";
import { useIntegrations } from "../hooks/useIntegrations";
import { formatDateTime, formatDateTimeCompact } from "../utils/formatDateTime";
import styles from "./AdminResources.module.css";
import { AdminPageIntro } from "../components/admin/AdminPageIntro";

const CATEGORIES: ReadonlyArray<{ value: ResourceCategory; label: string }> = [
  { value: "tutorial", label: "Tutorial" },
  { value: "concept", label: "Concept" },
  { value: "troubleshooting", label: "Troubleshooting" },
  { value: "update", label: "Update" },
  { value: "inspiration", label: "Inspiration" },
];

type FormState = {
  title: string;
  slug: string;
  category: ResourceCategory;
  description: string;
  videoUrl: string;
  durationSeconds: string;
  thumbnailUrl: string;
  bodyMarkdown: string;
  // Dashboard slug of the integration this resource guides, "" for none.
  integrationSlug: string;
  published: boolean;
  sortOrder: string;
};

const EMPTY_FORM: FormState = {
  title: "",
  slug: "",
  category: "tutorial",
  description: "",
  videoUrl: "",
  durationSeconds: "",
  thumbnailUrl: "",
  bodyMarkdown: "",
  integrationSlug: "",
  published: true,
  sortOrder: "0",
};

const slugifyTitle = (title: string): string =>
  title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 64)
    .replace(/^-+|-+$/g, "");

const formatTime = (iso: string): string => {
  if (!iso) return "—";
  const formatted = formatDateTime(iso);
  return formatted === "" ? "—" : formatted;
};

const safeInt = (raw: string, fallback: number | null): number | null => {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
};

const toWrite = (state: FormState): AdminResourceWrite => ({
  slug: state.slug.trim() || undefined,
  title: state.title.trim(),
  description: state.description.trim() ? state.description.trim() : null,
  category: state.category,
  format: "video",
  videoUrl: state.videoUrl.trim() ? state.videoUrl.trim() : null,
  durationSeconds: state.durationSeconds.trim() ? safeInt(state.durationSeconds, null) : null,
  thumbnailUrl: state.thumbnailUrl.trim() ? state.thumbnailUrl.trim() : null,
  bodyMarkdown: state.bodyMarkdown.trim() ? state.bodyMarkdown : null,
  integrationSlug: state.integrationSlug.trim() ? state.integrationSlug.trim() : null,
  published: state.published,
  sortOrder: safeInt(state.sortOrder, 0) ?? 0,
});

const fromResource = (r: ResourceView): FormState => ({
  title: r.title,
  slug: r.slug,
  category: r.category,
  description: r.description ?? "",
  videoUrl: r.videoUrl ?? "",
  durationSeconds: r.durationSeconds !== null ? String(r.durationSeconds) : "",
  thumbnailUrl: r.thumbnailUrl ?? "",
  bodyMarkdown: r.bodyMarkdown ?? "",
  integrationSlug: r.integrationSlug ?? "",
  published: r.published,
  sortOrder: String(r.sortOrder),
});

export const AdminResources = (): JSX.Element => {
  const { status, resources, error, create, update, remove } = useAdminResources();
  // Catalog view (tenant-less) purely to populate the guide picker. A guide
  // points at a dashboard slug, so the list must be the same one users browse.
  const { integrations } = useIntegrations(null);
  const integrationOptions = useMemo(
    () => [...integrations].sort((a, b) => a.title.localeCompare(b.title)),
    [integrations],
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(true);
  const [slugDirty, setSlugDirty] = useState<boolean>(false);
  const errorBannerRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    if (formError !== null) {
      errorBannerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [formError]);

  const formOpen = editingId !== null || creating;

  // Auto-suggest slug from title only when slug hasn't been edited manually
  // (and we're creating, not editing).
  useEffect(() => {
    if (editingId !== null) return;
    if (slugDirty) return;
    setForm((prev) => ({ ...prev, slug: slugifyTitle(prev.title) }));
  }, [form.title, slugDirty, editingId]);

  const beginEdit = (resource: ResourceView): void => {
    setEditingId(resource.id);
    setCreating(false);
    setForm(fromResource(resource));
    setSlugDirty(true);
    setFormError(null);
    setFlash(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const beginCreate = (): void => {
    setEditingId(null);
    setCreating(true);
    setForm(EMPTY_FORM);
    setSlugDirty(false);
    setFormError(null);
    setFlash(null);
  };

  const resetForm = (): void => {
    setEditingId(null);
    setCreating(false);
    setForm(EMPTY_FORM);
    setSlugDirty(false);
    setFormError(null);
  };

  const onSubmit = async (evt: FormEvent<HTMLFormElement>): Promise<void> => {
    evt.preventDefault();
    if (!form.title.trim()) {
      setFormError("Title is required.");
      return;
    }
    setSubmitting(true);
    setFormError(null);
    const write = toWrite(form);
    const result = editingId
      ? await update(editingId, write)
      : await create(write);
    setSubmitting(false);

    if (result.ok) {
      setFlash(
        editingId
          ? `Updated "${result.value.title}".`
          : `Created "${result.value.title}".`,
      );
      resetForm();
    } else {
      setFormError(result.message);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!confirmDeleteId) return;
    const result = await remove(confirmDeleteId);
    if (!result.ok) {
      setFormError(result.message);
    } else {
      setFlash("Resource deleted.");
      if (editingId === confirmDeleteId) resetForm();
    }
    setConfirmDeleteId(null);
  };

  const deleteTarget = useMemo(
    () => resources.find((r) => r.id === confirmDeleteId) ?? null,
    [resources, confirmDeleteId],
  );

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Resources">
        Videos shown on customer dashboards: a Loom or YouTube embed with optional Markdown notes.
      </AdminPageIntro>

      {!formOpen && (
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={beginCreate}
          >
            New resource
          </button>
          {flash && <span className={styles.flash}>{flash}</span>}
        </div>
      )}

      {formOpen && (
      <section className={styles.formSection}>
        <div className={styles.formHeader}>
          <h3 className={styles.formTitle}>
            {editingId ? "Edit resource" : "Create new resource"}
          </h3>
          <button type="button" className={styles.linkBtn} onClick={resetForm}>
            Close form
          </button>
        </div>
        <form className={styles.form} onSubmit={onSubmit}>
          <div className={styles.gridTwo}>
            <label className={styles.field}>
              <span className={styles.label}>Title</span>
              <input
                className={styles.input}
                type="text"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Workspace Files: Agents and Identity"
                disabled={submitting}
                required
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Slug</span>
              <input
                className={styles.input}
                type="text"
                value={form.slug}
                onChange={(e) => {
                  setSlugDirty(true);
                  setForm((p) => ({ ...p, slug: e.target.value }));
                }}
                placeholder="auto-generated from title"
                disabled={submitting}
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Category</span>
              <select
                className={styles.input}
                value={form.category}
                onChange={(e) =>
                  setForm((p) => ({ ...p, category: e.target.value as ResourceCategory }))
                }
                disabled={submitting}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Video URL</span>
              <input
                className={styles.input}
                type="url"
                value={form.videoUrl}
                onChange={(e) => setForm((p) => ({ ...p, videoUrl: e.target.value }))}
                placeholder="https://www.loom.com/share/... or https://www.youtube.com/watch?v=..."
                disabled={submitting}
                required
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>
                Duration <span className={styles.optional}>(seconds, optional)</span>
              </span>
              <input
                className={styles.input}
                type="number"
                min="0"
                value={form.durationSeconds}
                onChange={(e) =>
                  setForm((p) => ({ ...p, durationSeconds: e.target.value }))
                }
                placeholder="e.g. 270"
                disabled={submitting}
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>
                Guide for <span className={styles.optional}>(optional integration)</span>
              </span>
              <select
                className={styles.input}
                value={form.integrationSlug}
                onChange={(e) => setForm((p) => ({ ...p, integrationSlug: e.target.value }))}
                disabled={submitting}
              >
                <option value="">Not a connect guide</option>
                {/* A slug saved before its integration left the catalog would
                    otherwise vanish from the select and be silently cleared on
                    the next save, so keep it selectable. */}
                {form.integrationSlug !== "" &&
                !integrationOptions.some((i) => i.slug === form.integrationSlug) ? (
                  <option value={form.integrationSlug}>{form.integrationSlug} (not in catalog)</option>
                ) : null}
                {integrationOptions.map((i) => (
                  <option key={i.slug} value={i.slug}>
                    {i.title}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.field}>
              <span className={styles.label}>
                Thumbnail URL <span className={styles.optional}>(optional)</span>
              </span>
              <input
                className={styles.input}
                type="url"
                value={form.thumbnailUrl}
                onChange={(e) =>
                  setForm((p) => ({ ...p, thumbnailUrl: e.target.value }))
                }
                placeholder="defaults to the video's own thumbnail"
                disabled={submitting}
              />
            </label>
          </div>

          <label className={styles.field}>
            <span className={styles.label}>
              Description <span className={styles.optional}>(short, shown on card)</span>
            </span>
            <input
              className={styles.input}
              type="text"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="One-line teaser shown beneath the title"
              disabled={submitting}
            />
          </label>

          <div className={styles.bodyHeader}>
            <span className={styles.label}>Body — Markdown</span>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => setShowPreview((v) => !v)}
              aria-pressed={showPreview}
            >
              {showPreview ? "Hide preview" : "Show preview"}
            </button>
          </div>
          <div
            className={`${styles.bodyArea} ${showPreview ? styles.bodyAreaSplit : ""}`}
          >
            <textarea
              className={styles.textarea}
              value={form.bodyMarkdown}
              onChange={(e) =>
                setForm((p) => ({ ...p, bodyMarkdown: e.target.value }))
              }
              placeholder={`# Heading\n\nSupports **bold**, _italic_, [links](https://...), \`code\`, and lists.`}
              rows={14}
              disabled={submitting}
            />
            {showPreview && (
              <div className={styles.preview} aria-label="Markdown preview">
                {form.bodyMarkdown.trim() ? (
                  <MarkdownView body={form.bodyMarkdown} />
                ) : (
                  <p className={styles.previewEmpty}>
                    Preview will appear here as you type.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className={styles.gridTwo}>
            <label className={styles.field}>
              <span className={styles.label}>
                Sort order <span className={styles.optional}>(lower = earlier)</span>
              </span>
              <input
                className={styles.input}
                type="number"
                value={form.sortOrder}
                onChange={(e) =>
                  setForm((p) => ({ ...p, sortOrder: e.target.value }))
                }
                disabled={submitting}
              />
            </label>

            <label className={styles.checkboxField}>
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) =>
                  setForm((p) => ({ ...p, published: e.target.checked }))
                }
                disabled={submitting}
              />
              <span>
                <span className={styles.checkboxTitle}>Published</span>
                <span className={styles.checkboxHint}>
                  Visible on the user dashboard. Uncheck to draft.
                </span>
              </span>
            </label>
          </div>

          {formError && (
            <p
              ref={errorBannerRef}
              className={styles.errorBanner}
              role="alert"
              aria-live="polite"
            >
              {formError}
            </p>
          )}
          {flash && !formError && <p className={styles.flash}>{flash}</p>}

          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={submitting || !form.title.trim() || !form.videoUrl.trim()}
            >
              {submitting
                ? editingId
                  ? "Saving…"
                  : "Creating…"
                : editingId
                  ? "Save changes"
                  : "Create resource"}
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={resetForm}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </section>
      )}

      <section className={styles.listSection}>
        <h3 className={styles.listTitle}>All resources</h3>

        {status === "loading" && (
          <AdminTableSkeleton
            headers={["Title", "Category", "Status", "Order", "Updated", ""]}
            tableClassName={styles.table ?? ""}
          />
        )}
        {status === "error" && error && <div className={styles.error}>{error}</div>}

        {status === "ready" && resources.length === 0 && (
          <div className={styles.empty}>
            No resources yet — create your first one above.
          </div>
        )}

        {status === "ready" && resources.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Order</th>
                <th>Updated</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {resources.map((r) => (
                <tr key={r.id} className={editingId === r.id ? styles.rowEditing : undefined}>
                  <td>
                    <Link to={`/dashboard/resources/${r.slug}`} className={styles.titleLink} title={r.title}>
                      {r.title}
                    </Link>
                    <div className={styles.slugHint} title={`/${r.slug}`}>
                      /{r.slug}
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.pill} ${styles[`pill_${r.category}`]}`}>
                      {r.category}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`${styles.statusPill} ${
                        r.published ? styles.statusPublished : styles.statusDraft
                      }`}
                    >
                      {r.published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className={styles.numCell}>{r.sortOrder}</td>
                  <td className={styles.nowrap} title={formatTime(r.updatedAt)}>
                    {formatDateTimeCompact(r.updatedAt)}
                  </td>
                  <td className={styles.actionCell}>
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      onClick={() => beginEdit(r)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={styles.dangerBtn}
                      onClick={() => setConfirmDeleteId(r.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {deleteTarget && (
        <div
          className={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-resource-title"
        >
          <div className={styles.modal}>
            <h3 className={styles.modalTitle} id="delete-resource-title">
              Delete this resource?
            </h3>
            <p className={styles.modalBody}>
              &ldquo;{deleteTarget.title}&rdquo; will be removed from the
              dashboard immediately. This cannot be undone.
            </p>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.dangerBtn}
                onClick={() => {
                  void confirmDelete();
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
